import * as THREE from 'three';
import { AbstractProjectile } from './AbstractProjectile';
import { LODLevel } from '../optimization/LODSystem';
import { SpatialManager } from '../optimization/SpatialManager';
import { GameObject } from '../objects/GameObject';
import { Damageable } from './ProjectileManager';

/**
 * Basic projectile implementation - simple straight-line movement
 */
export class BasicProjectile extends AbstractProjectile {
  private radius: number = 0.1;
  private collisionRadius: number = 0.15;
  private spatialManager: SpatialManager | null = null;
  
  /**
   * Creates a new BasicProjectile
   * @param scene The scene to add the mesh to
   */
  constructor(scene: THREE.Scene) {
    super(scene);
    this.damage = 25;
    this.lifetime = 5.0;
    
    // Create meshes for different LOD levels
    this.createLODMeshes();
  }
  
  /**
   * Set the spatial manager for collision detection
   */
  public setSpatialManager(spatialManager: SpatialManager): void {
    this.spatialManager = spatialManager;
  }
  
  /**
   * Creates the high, medium, and low detail meshes
   */
  private createLODMeshes(): void {
    // Use more vibrant colors for better visibility
    const mainColor = 0xff3333;  // Bright red
    const emissiveColor = 0xff0000; // Red glow
    const emissiveIntensity = 1.0; // Brighter glow
    
    // Create material with glow effect for better visibility
    const material = new THREE.MeshStandardMaterial({ 
      color: mainColor,
      emissive: emissiveColor,
      emissiveIntensity: emissiveIntensity,
      metalness: 0.5,
      roughness: 0.2,
      flatShading: true
    });
    
    // HIGH detail mesh
    const highGeometry = new THREE.SphereGeometry(this.radius, 16, 16);
    const highMesh = new THREE.Mesh(highGeometry, material);
    this.lodMeshes.set(LODLevel.HIGH, highMesh);
    
    // MEDIUM detail mesh
    const medGeometry = new THREE.SphereGeometry(this.radius, 12, 12);
    const medMesh = new THREE.Mesh(medGeometry, material);
    this.lodMeshes.set(LODLevel.MEDIUM, medMesh);
    
    // LOW detail mesh
    const lowGeometry = new THREE.SphereGeometry(this.radius, 8, 8);
    const lowMesh = new THREE.Mesh(lowGeometry, material);
    this.lodMeshes.set(LODLevel.LOW, lowMesh);
    
    // Set the high detail mesh as the default
    this.mesh = highMesh;
  }
  
  /**
   * Creates the mesh for this projectile
   */
  public createMesh(): void {
    // Mesh already created in constructor, but we need this method to satisfy
    // the abstract class requirement. It might be called if the mesh is disposed.
    if (!this.mesh) {
      this.createLODMeshes();
    }
  }
  
  /**
   * Update the projectile
   * @param deltaTime Time since last update in seconds
   * @returns Whether the projectile is still active
   */
  public override update(deltaTime: number): boolean {
    // Call parent update - handles basic movement
    if (!super.update(deltaTime)) {
      return false;
    }
    
    // Any additional BasicProjectile-specific updates
    this.checkCollisions();
    
    return this.active;
  }
  
  /**
   * Perform a simple collision check
   * More sophisticated implementations might use a spatial partitioning system
   */
  private checkCollisions(): void {
    try {
      // Check collision with floor
      if (this.position.y <= this.radius) {
        // Hit the floor
        this.onImpact({ type: 'floor', position: new THREE.Vector3(this.position.x, 0, this.position.z) });
        return; // End collision check if we hit the floor
      }
      
      // If we have a spatial manager, use it for more efficient collision detection
      if (this.spatialManager) {
        // Get nearby objects within collision radius
        const nearbyObjects = this.spatialManager.getObjectsInRadius(
          this.position,
          this.collisionRadius * 3 // Use slightly larger radius for better detection
        );
        
        if (nearbyObjects.length > 0) {
          console.log(`Found ${nearbyObjects.length} potential collision objects near projectile`);
        }
        
        // Check for collisions with each nearby object
        for (const object of nearbyObjects) {
          try {
            if (this.collidesWithGameObject(object)) {
              console.log(`✅ Collision detected with ${object.constructor.name}`);
              this.onImpact(object);
              return; // End collision check after first hit
            }
          } catch (collisionError) {
            console.error(`Error during collision check with ${object.constructor.name}:`, collisionError);
          }
        }
      }
    } catch (error) {
      console.error('Error in projectile collision detection:', error);
    }
  }
  
  /**
   * Check if this projectile collides with a GameObject
   */
  private collidesWithGameObject(object: GameObject): boolean {
    // Simple distance-based collision check
    const objectPosition = object.getPosition();
    const distanceSquared = this.position.distanceToSquared(objectPosition);
    
    // Log collision check attempt
    console.log(`Checking collision with ${object.constructor.name}:`, {
      projectilePosition: this.position.clone(),
      objectPosition: objectPosition.clone(),
      distance: Math.sqrt(distanceSquared),
      hasBoundingVolume: !!object.getBoundingVolume()
    });
    
    // SIMPLIFY: Use direct distance-based collision instead of bounding volume checks
    // This avoids the issues with the BoundingVolume interface
    
    // Get object's scale to estimate collision radius
    const objectScale = object.getScale();
    const objectRadius = Math.max(objectScale.x, objectScale.y, objectScale.z) * 0.5;
    const collisionDistance = this.collisionRadius + objectRadius;

    // Check if distance is within collision range
    const collision = distanceSquared <= (collisionDistance * collisionDistance);
    
    if (collision) {
      console.log(`Direct distance collision detected with ${object.constructor.name} - distance: ${Math.sqrt(distanceSquared)}, threshold: ${collisionDistance}`);
    }
    
    return collision;
  }
  
  /**
   * Handle projectile impact with a target
   * @param target The object that was hit
   */
  public onImpact(target: any): void {
    const targetName = target ? (target.constructor ? target.constructor.name : (typeof target === 'object' ? JSON.stringify(target) : typeof target)) : 'unknown';
    
    // Log successful impact with better information
    console.log(`🎯 Projectile impact with ${targetName}:`, {
      targetType: targetName,
      targetPosition: target && typeof target.getPosition === 'function' ? target.getPosition().clone() : 'unknown',
      projectilePosition: this.position.clone(),
      damage: this.damage,
      isDamageable: target && typeof target.takeDamage === 'function'
    });
    
    // Apply damage if target can take damage
    if (target && typeof target.takeDamage === 'function') {
      try {
        target.takeDamage(this.damage);
        console.log(`✅ Applied ${this.damage} damage to ${targetName}`);
      } catch (error) {
        console.error(`Failed to apply damage to ${targetName}:`, error);
      }
    } else {
      console.log(`⚠️ No damage applied to ${targetName} - object does not implement takeDamage()`);
    }
    
    // Deactivate projectile
    this.deactivate();
  }
  
  /**
   * Get the collision radius for this projectile
   */
  public getCollisionRadius(): number {
    return this.collisionRadius;
  }
} 